/**
 * OnboardingDone.jsx — the celebration (s95 sign-up rebuild).
 *
 * Keeps the v213.5 moment Dr. Rami asked to keep (confetti, the bobbing Orella mark in
 * its ICE_BLUE breathe ring) and changes what it says and what it does:
 *   · "Welcome to Orella, {name}" — no more completion percentage; sign-up is complete.
 *   · It no longer auto-navigates. Two choices: Start my first case (primary) or set up
 *     the health profile first. Both run the one-time How Orella works tour next.
 *   · It names the three items a case needs before it can be shared — insurance (or
 *     self-pay), allergies, medications — and says they can wait.
 *
 * Caller writes the profile (onboardingCompleted = true) BEFORE this mounts.
 */
import { useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import orellaOIcon from '../../assets/logos/orella_o_mark.png'
import {
  OnboardingShell,
  PrimaryButton,
  SecondaryButton,
  Footer,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  TEXT_MUTED,
  BORDER_LIGHT,
} from './onboardingPrimitives'

export default function OnboardingDone({ onStartCase, onSetupProfile }) {
  const { t } = useI18n()
  const user = useUserStore((s) => s.user)
  const firstName = user?.firstName || ''

  const confetti = useMemo(() => {
    const colors = [BLUE, NAVY, GREEN, LIGHT_BLUE]
    return Array.from({ length: 22 }).map((_, i) => ({
      id: i,
      color: colors[i % 4],
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 1.8 + Math.random(),
    }))
  }, [])

  const items = [
    { key: 'insurance', label: t('onboarding.done.itemInsurance', 'Insurance'), sub: t('onboarding.done.itemInsuranceSub', "We're looking it up for you") },
    { key: 'allergies', label: t('onboarding.done.itemAllergies', 'Allergies') },
    { key: 'medications', label: t('onboarding.done.itemMeds', 'Medications') },
  ]

  return (
    <OnboardingShell>
      <div className="flex-1 flex flex-col px-6 pt-14 pb-2 relative" style={{ overflow: 'hidden' }}>
        {confetti.map((c) => (
          <div
            key={c.id}
            aria-hidden="true"
            style={{
              position: 'absolute', left: `${c.left}%`, top: -20, width: 7, height: 12, borderRadius: 2,
              background: c.color, animation: `confetti-fall ${c.duration}s ease-in forwards`, animationDelay: `${c.delay}s`,
            }}
          />
        ))}

        <div className="orella-bob" style={{ marginBottom: 30, zIndex: 10 }}>
          <div
            className="rounded-full flex items-center justify-center breathe-green"
            style={{ width: 80, height: 80, background: ICE_BLUE, border: `2px solid ${LIGHT_BLUE}` }}
          >
            <img src={orellaOIcon} alt="" style={{ width: 46, height: 46, objectFit: 'contain' }} />
          </div>
        </div>

        <h1 style={{ fontSize: 31, lineHeight: 1.1, letterSpacing: '-0.02em', color: NAVY, fontWeight: 700, marginBottom: 12, zIndex: 10 }}>
          {firstName
            ? t('onboarding.done.welcomeNamed', { defaultValue: 'Welcome to Orella, {{name}}', name: firstName })
            : t('onboarding.done.welcome', 'Welcome to Orella')}
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: TEXT_MUTED, marginBottom: 18, zIndex: 10 }}>
          {t('onboarding.done.body', 'Your account is ready. Three things are needed before you share your first case, and they can wait.')}
        </p>

        <div style={{ border: `1.5px solid ${BORDER_LIGHT}`, borderRadius: 16, background: '#fff', zIndex: 10 }}>
          {items.map((it, i) => (
            <div key={it.key} className="flex items-center justify-between"
              style={{ padding: '12px 14px', borderTop: i ? `1px solid ${BORDER_LIGHT}` : 'none' }}>
              <span style={{ fontSize: 14.5, color: NAVY }}>
                {it.label}
                {it.sub && <span style={{ display: 'block', fontSize: 12.5, color: TEXT_MUTED }}>{it.sub}</span>}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: BLUE, whiteSpace: 'nowrap' }}>
                {t('onboarding.done.beforeSharing', 'Before sharing')}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Footer>
        <PrimaryButton variant="blue" onClick={onStartCase}>
          {t('onboarding.done.showMe', 'See how Orella works')}
        </PrimaryButton>
        <div style={{ height: 10 }} />
        <SecondaryButton onClick={onSetupProfile}>
          {t('onboarding.done.setupProfile', 'Set up my health profile first')}
        </SecondaryButton>
      </Footer>

      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-30px) rotate(0deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translateY(680px) rotate(720deg); opacity: 0; }
        }
        @keyframes orella-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        .orella-bob { animation: orella-bob 3s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .orella-bob { animation: none; }
        }
      `}</style>
    </OnboardingShell>
  )
}
